/**
 * Notifications component - displays toast notifications for API errors and successes
 */

import { useEffect, useState } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

interface Notification {
    id: number;
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
    timestamp: number;
}

let notificationId = 0;

export default function Notifications() {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    useEffect(() => {
        const handleError = (event: CustomEvent<{ message: string; type: string }>) => {
            const newNotification: Notification = {
                id: ++notificationId,
                message: event.detail.message,
                type: 'error',
                timestamp: Date.now()
            };
            setNotifications(prev => [...prev, newNotification]);
        };

        const handleSuccess = (event: CustomEvent<{ message: string; type: string }>) => {
            const newNotification: Notification = {
                id: ++notificationId,
                message: event.detail.message,
                type: 'success',
                timestamp: Date.now()
            };
            setNotifications(prev => [...prev, newNotification]);
        };

        window.addEventListener('api-error', handleError as EventListener);
        window.addEventListener('api-success', handleSuccess as EventListener);

        return () => {
            window.removeEventListener('api-error', handleError as EventListener);
            window.removeEventListener('api-success', handleSuccess as EventListener);
        };
    }, []);

    const removeNotification = (id: number) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const getVariant = (type: Notification['type']) => {
        switch (type) {
            case 'error':
                return 'danger';
            case 'success':
                return 'success';
            case 'warning':
                return 'warning';
            case 'info':
                return 'info';
            default:
                return 'secondary';
        }
    };

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'error':
                return 'bi-exclamation-circle-fill';
            case 'success':
                return 'bi-check-circle-fill';
            case 'warning':
                return 'bi-exclamation-triangle-fill';
            case 'info':
                return 'bi-info-circle-fill';
            default:
                return 'bi-bell-fill';
        }
    };

    return (
        <ToastContainer
            position="top-end"
            className="p-3"
            style={{ zIndex: 9999, position: 'fixed' }}
        >
            {notifications.map(notification => (
                <Toast
                    key={notification.id}
                    onClose={() => removeNotification(notification.id)}
                    show={true}
                    delay={5000}
                    autohide
                    bg={getVariant(notification.type)}
                >
                    <Toast.Header>
                        <i className={`bi ${getIcon(notification.type)} me-2`}></i>
                        <strong className="me-auto">
                            {notification.type === 'error' ? 'Error' :
                             notification.type === 'success' ? 'Success' :
                             notification.type === 'warning' ? 'Warning' : 'Info'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={notification.type === 'error' || notification.type === 'success' ? 'text-white' : ''}>
                        {notification.message}
                    </Toast.Body>
                </Toast>
            ))}
        </ToastContainer>
    );
}
